import { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Async } from 'react-select-plus';
import msStyles from '../../../../../../admin/shared-components/MultiSelect/MultiSelect.module.css';
import styles from './PersonaSelect.module.css';
import { PersonaRowData, PersonaSearchResponse } from 'admin/types';
import { mobxInjectSelect } from 'common/utils';
import { useAppSelector } from 'redux-stores';

type MobxProps = {
  findPersonas: (params: {
    query: string;
    page: number;
    pageSize: number;
    selectedOrganizationId: string;
  }) => Promise<PersonaSearchResponse>;
};

type PersonaSelectProps = {
  dataTestId?: string;
  onChange?: (selectedOption: PersonaRowData) => void;
  placeholder?: string;
  id?: string;
};

const PersonaSelect = ({
  dataTestId,
  findPersonas,
  onChange,
  placeholder = 'Select your Persona...',
  id,
}: PersonaSelectProps & MobxProps) => {
  const selectRef = useRef<{ input?: { input?: HTMLInputElement } }>();
  const [selectValue, setSelectValue] = useState<PersonaRowData>();
  const selectedOrganizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const loadOptionsRef = useRef<{ [key: string]: Promise<{ options: PersonaRowData[] }> }>({});
  useEffect(() => {
    if (!dataTestId) return;
    selectRef.current?.input?.input?.setAttribute('data-test-id', dataTestId);
  }, [dataTestId]);

  useEffect(() => {
    loadOptionsRef.current = {};
  }, [selectedOrganizationId]);

  const onValueChange = (option: PersonaRowData) => {
    setSelectValue(option);
    onChange && onChange(option);
  };

  const loadOptions = useCallback(
    async (
      x: string,
      callback: (error: string | null, data: { options: PersonaRowData[] }) => void
    ) => {
      const cacheKey = `${selectedOrganizationId}:${x}`;
      if (cacheKey in loadOptionsRef.current) {
        try {
          const cachedResult = await loadOptionsRef.current[cacheKey];
          callback(null, cachedResult);
        } catch (error) {
          delete loadOptionsRef.current[cacheKey];
          callback(error instanceof Error ? error.message : 'An error occurred', { options: [] });
        }
        return;
      }

      const promise = findPersonas({
        page: 1,
        query: x,
        pageSize: 50,
        selectedOrganizationId,
      }).then(({ data }) => {
        const result = { options: data };
        // Cache the result for this query + org
        loadOptionsRef.current[cacheKey] = Promise.resolve(result);
        return result;
      });

      // Store the promise immediately to prevent duplicate calls
      loadOptionsRef.current[cacheKey] = promise;

      try {
        const result = await promise;
        callback(null, result);
      } catch (error) {
        // Remove from cache on error so it can be retried
        delete loadOptionsRef.current[cacheKey];
        callback(error instanceof Error ? error.message : 'An error occurred', { options: [] });
      }
    },
    [findPersonas, selectedOrganizationId]
  );

  return (
    <Async
      id={id}
      className={classnames(msStyles.multiSelect, styles.personaSelect)}
      autoBlur={true}
      escapeClearsValue={false}
      cache={false}
      menuStyle={{
        overflow: 'scroll',
        height: 200,
      }}
      onChange={onValueChange}
      openOnClick={true}
      optionClassName={msStyles.multiSelectOption}
      labelKey="name"
      valueKey="name"
      loadOptions={loadOptions}
      placeholder={placeholder}
      ref={selectRef}
      searchable={true}
      value={selectValue}
    />
  );
};

export default mobxInjectSelect<PersonaSelectProps, MobxProps>({
  personaStore: ['findPersonas'],
})(PersonaSelect);
